import type { ReactNode } from 'react';
import type { BadgeProps } from '@shared/components/ui/badge';

import { Badge } from '@shared/components/ui/badge';

export type SectionHeaderCountColor = 'default' | 'primary' | 'info' | 'success' | 'warning' | 'error';

const COUNT_VARIANTS: Record<SectionHeaderCountColor, NonNullable<BadgeProps['variant']>> = {
  default: 'secondary',
  primary: 'soft',
  info: 'soft',
  success: 'success',
  warning: 'warning',
  error: 'destructive',
};

type Props = {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  count?: number;
  countColor?: SectionHeaderCountColor;
  action?: ReactNode;
};

/**
 * Card header: icon + title (+ counter) + subtitle on the left, free actions on the right.
 */
export const SectionHeader = ({ title, subtitle, icon, count, countColor = 'primary', action }: Props) => (
  <div className="flex items-center gap-3 px-5 py-4">
    {icon && <div className="flex shrink-0 text-xl text-muted-foreground">{icon}</div>}
    <div className="min-w-0 grow">
      <div className="flex items-center gap-2">
        <h2 className="truncate text-lg leading-7 font-semibold">{title}</h2>
        {count !== undefined && (
          <Badge size="sm" variant={COUNT_VARIANTS[countColor]}>
            {count}
          </Badge>
        )}
      </div>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
    </div>
    {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
  </div>
);
